import { getApiUrl } from "../../config/api";

const LIVE_INTERVIEW_ACTOR_EMAIL_KEYS = [
  "admin_email",
  "employer_email",
  "recruiter_email",
  "company_email",
  "organizer_email",
];

function getStoredActorEmail(key) {
  if (typeof window === "undefined") return "";
  return window.sessionStorage.getItem(key) || window.localStorage.getItem(key) || "";
}

function getLiveInterviewActorEmail() {
  if (typeof window === "undefined") return "";
  for (const key of LIVE_INTERVIEW_ACTOR_EMAIL_KEYS) {
    const value = getStoredActorEmail(key);
    if (value) return value;
  }
  return "";
}

function buildRequestHeaders(headers = {}) {
  return new Headers(headers);
}

function buildActorRequestHeaders(headers = {}, actorEmail = "", { allowStoredActorEmail = true } = {}) {
  const requestHeaders = buildRequestHeaders(headers);
  const effectiveActorEmail =
    actorEmail || (allowStoredActorEmail ? getLiveInterviewActorEmail() : "");
  if (effectiveActorEmail && !requestHeaders.has("X-Admin-Email")) {
    requestHeaders.set("X-Admin-Email", effectiveActorEmail);
  }
  return requestHeaders;
}

async function readJsonResponse(response) {
  const text = await response.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    const looksLikeHtml = /^\s*<!doctype html|^\s*<html[\s>]/i.test(text);
    const status = response.status;
    return {
      error: looksLikeHtml
        ? `Server returned HTTP ${status} with HTML instead of JSON. Check that the app points at the API backend (REACT_APP_API_URL) and that mute routes are deployed.`
        : text,
      raw_error: text,
      response_type: looksLikeHtml ? "html" : "text",
    };
  }
}

async function requestJson(endpoint, options = {}) {
  const url = getApiUrl(endpoint);
  const method = options.method || "GET";
  const { headers, suppressAbortLog = false, ...requestOptions } = options;
  let response;

  try {
    response = await fetch(url, {
      credentials: "include",
      ...requestOptions,
      headers: buildRequestHeaders(headers),
    });
  } catch (err) {
    if (err?.name === "AbortError" && suppressAbortLog) {
      const abortedError = new Error("Request cancelled.");
      abortedError.name = "AbortError";
      abortedError.code = "REQUEST_ABORTED";
      abortedError.endpoint = endpoint;
      abortedError.url = url;
      abortedError.method = method;
      abortedError.originalError = err;
      throw abortedError;
    }
    console.error("[LiveInterview] API network failure", {
      endpoint,
      url,
      method,
      error: err,
    });
    const wrappedError = new Error(
      `Network request failed for ${method} ${url}. Check that the backend is running and the frontend proxy/CORS can reach it.`
    );
    wrappedError.endpoint = endpoint;
    wrappedError.url = url;
    wrappedError.method = method;
    wrappedError.originalError = err;
    throw wrappedError;
  }

  const payload = await readJsonResponse(response);
  if (!response.ok) {
    console.error("[LiveInterview] API request failed", {
      endpoint,
      url,
      method,
      status: response.status,
      backendMessage: payload.error,
      payload,
    });
    const error = new Error(payload.error || `Request failed (${response.status})`);
    error.endpoint = endpoint;
    error.url = url;
    error.method = method;
    error.status = response.status;
    error.backendMessage = payload.error;
    error.payload = payload;
    error.responseType = payload.response_type;
    throw error;
  }
  return payload;
}

function requireRoomId(roomId, action) {
  const safeRoomId = String(roomId || "").trim();
  if (!safeRoomId) {
    throw new Error(`Live interview room id is required before ${action}.`);
  }
  return safeRoomId;
}

function roomEndpoint(roomId, suffix = "") {
  const safeRoomId = requireRoomId(roomId, "calling the live interview API");
  return `/api/live-interview/rooms/${encodeURIComponent(safeRoomId)}${suffix}`;
}

export function lookupLiveInterviewRoom({ jobId, candidateEmail }) {
  const params = new URLSearchParams({
    job_id: String(jobId || ""),
    candidate_email: candidateEmail || "",
  });
  return requestJson(`/api/live-interview/rooms?${params.toString()}`);
}

export function createLiveInterviewRoom(payload) {
  return requestJson("/api/live-interview/rooms", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function getLiveInterviewRoom(roomId, {
  inviteToken,
  interviewerToken,
  actorEmail,
  actorIdentity,
  role,
} = {}) {
  const params = new URLSearchParams();
  if (inviteToken) {
    params.set("invite_token", inviteToken);
  }
  if (interviewerToken) {
    params.set("interviewer_token", interviewerToken);
  }
  const effectiveActorEmail = role === "candidate" ? "" : actorEmail || getLiveInterviewActorEmail();
  if (effectiveActorEmail) {
    params.set("actor_email", effectiveActorEmail);
  }
  if (actorIdentity) {
    params.set("actor_identity", actorIdentity);
  }
  if (role) {
    params.set("role", role);
  }
  const query = params.toString();
  return requestJson(`${roomEndpoint(roomId)}${query ? `?${query}` : ""}`, {
    headers: buildActorRequestHeaders({}, effectiveActorEmail),
  });
}

export function updateLiveInterviewRoomSettings(roomId, payload) {
  return requestJson(roomEndpoint(roomId, "/settings"), {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function createLiveInterviewCandidateInvite(roomId, payload) {
  return requestJson(roomEndpoint(roomId, "/candidate-invites"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function updateLiveInterviewCandidateInvite(roomId, inviteId, payload) {
  const safeInviteId = String(inviteId || "").trim();
  if (!safeInviteId) {
    throw new Error("Candidate invite id is required before updating an invite.");
  }
  return requestJson(roomEndpoint(roomId, `/candidate-invites/${encodeURIComponent(safeInviteId)}`), {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function getLiveInterviewJoinRequests(roomId, { actorEmail, actorIdentity, role = "interviewer", status = "pending" } = {}) {
  const effectiveActorEmail = role === "candidate" ? "" : actorEmail || getLiveInterviewActorEmail();
  const params = new URLSearchParams({
    actor_email: effectiveActorEmail || "",
    role,
    status,
  });
  if (actorIdentity) {
    params.set("actor_identity", actorIdentity);
  }
  return requestJson(roomEndpoint(roomId, `/join-requests?${params.toString()}`), {
    headers: buildActorRequestHeaders({}, effectiveActorEmail),
  });
}

export function respondLiveInterviewJoinRequest(roomId, requestId, payload) {
  const effectiveActorEmail =
    payload?.role === "candidate"
      ? ""
      : payload?.actor_email || payload?.email || getLiveInterviewActorEmail();
  const headers = buildActorRequestHeaders({ "Content-Type": "application/json" }, effectiveActorEmail);
  if (payload?.actor_identity && !headers.has("X-Live-Participant-Identity")) {
    headers.set("X-Live-Participant-Identity", payload.actor_identity);
  }
  return requestJson(roomEndpoint(roomId, `/join-requests/${encodeURIComponent(requestId)}`), {
    method: "PATCH",
    headers,
    body: JSON.stringify(payload),
  });
}

export function getLiveInterviewInvite(inviteToken) {
  return requestJson(`/api/live-interview/invites/${encodeURIComponent(inviteToken)}`);
}

/**
 * Exchange an interviewer_action_token for the verified interviewer context
 * (email, room_id, booking_id, names, slot). Used so an external interviewer
 * (no SearchJob login) can open the email link, prove they own the host email,
 * and act as host in the live interview room.
 */
export function fetchInterviewerContextByToken(token) {
  const safeToken = String(token || "").trim();
  if (!safeToken) {
    return Promise.reject(new Error("Interviewer token is required."));
  }
  const params = new URLSearchParams({ token: safeToken });
  return requestJson(`/api/meeting/booking/interviewer-context?${params.toString()}`);
}

export function requestLiveInterviewInviteJoin(inviteToken, payload = {}) {
  return requestJson(`/api/live-interview/invites/${encodeURIComponent(inviteToken)}/join-requests`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function getLiveInterviewInviteJoinRequest(inviteToken, requestId) {
  return requestJson(
    `/api/live-interview/invites/${encodeURIComponent(inviteToken)}/join-requests/${encodeURIComponent(requestId)}`
  );
}

export function getLiveInterviewJoinEligibility(roomId, payload = {}) {
  const effectiveActorEmail =
    payload.role === "candidate"
      ? ""
      : payload.email || payload.actor_email || getLiveInterviewActorEmail();
  const params = new URLSearchParams();
  if (payload.role) params.set("role", payload.role);
  if (effectiveActorEmail) {
    params.set("email", effectiveActorEmail);
  }
  if (payload.actor_identity) params.set("actor_identity", payload.actor_identity);
  if (payload.invite_token || payload.inviteToken) {
    params.set("invite_token", payload.invite_token || payload.inviteToken);
  }
  const query = params.toString();
  const suffix = query ? `/join-eligibility?${query}` : "/join-eligibility";
  return requestJson(roomEndpoint(roomId, suffix), {
    method: "GET",
    headers: buildActorRequestHeaders({}, effectiveActorEmail, {
      allowStoredActorEmail: payload.role !== "candidate",
    }),
  });
}

export function requestLiveInterviewJoinToken(roomId, payload) {
  return requestJson(roomEndpoint(roomId, "/join-token"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function startLiveInterviewRecording(roomId, payload = {}) {
  return requestJson(roomEndpoint(roomId, "/recording/start"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function stopLiveInterviewRecording(roomId, payload = {}) {
  return requestJson(roomEndpoint(roomId, "/recording/stop"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function resolveLiveInterviewRecordingDownloadUrl(payload) {
  const download = payload?.download;
  if (download && typeof download === "object") {
    const nestedUrl = download.url || download.download_url;
    if (nestedUrl) {
      return String(nestedUrl).trim();
    }
  }
  return String(payload?.download_url || payload?.url || "").trim();
}

export function getLiveInterviewRecordingDownloadUrl(roomId, recordingId, { actorEmail, actorIdentity, role = "interviewer" } = {}) {
  const safeRecordingId = String(recordingId || "").trim();
  if (!safeRecordingId) {
    throw new Error("Recording id is required before requesting a download URL.");
  }
  const effectiveActorEmail = role === "candidate" ? "" : actorEmail || getLiveInterviewActorEmail();
  const params = new URLSearchParams({
    actor_email: effectiveActorEmail || "",
    role: role || "interviewer",
  });
  if (actorIdentity) {
    params.set("actor_identity", actorIdentity);
  }
  return requestJson(
    `${roomEndpoint(roomId)}/recordings/${encodeURIComponent(safeRecordingId)}/download-url?${params.toString()}`,
    {
      headers: buildActorRequestHeaders({}, effectiveActorEmail),
    }
  );
}

function normalizeTranscriptPdfPayload(payload) {
  const pdfUrl = String(payload?.pdf_url || payload?.transcript_pdf?.url || "").trim();
  return {
    ...payload,
    pdf_url: pdfUrl,
    transcript_pdf: {
      ...(payload?.transcript_pdf || {}),
      url: pdfUrl,
      expires_in_seconds:
        payload?.expires_in ?? payload?.transcript_pdf?.expires_in_seconds ?? null,
    },
  };
}

export async function getLiveInterviewTranscriptPdfUrl(
  roomId,
  { actorEmail, actorIdentity, role = "interviewer" } = {}
) {
  const effectiveActorEmail = role === "candidate" ? "" : actorEmail || getLiveInterviewActorEmail();
  const params = new URLSearchParams({
    actor_email: effectiveActorEmail || "",
    role: role || "interviewer",
  });
  if (actorIdentity) {
    params.set("actor_identity", actorIdentity);
  }
  const headers = buildActorRequestHeaders({}, effectiveActorEmail);
  const query = params.toString();
  const base = roomEndpoint(roomId);
  try {
    const payload = await requestJson(`${base}/transcript/pdf-url?${query}`, { headers });
    return normalizeTranscriptPdfPayload(payload);
  } catch (primaryError) {
    try {
      const payload = await requestJson(`${base}/transcript-pdf-url?${query}`, { headers });
      return normalizeTranscriptPdfPayload(payload);
    } catch {
      throw primaryError;
    }
  }
}

export function removeLiveInterviewParticipant(roomId, payload = {}) {
  return requestJson(roomEndpoint(roomId, "/participants/remove"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

/** Mute candidates: use payload.mute_all, or payload.identity, or payload.identities (array of LiveKit identities). */
export function muteLiveInterviewCandidates(roomId, payload = {}) {
  return requestJson(roomEndpoint(roomId, "/participants/mute-candidates"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

/** Mute or unmute every connected candidate (server-side LiveKit). Uses mute-candidates so proxies/old stacks that block /mute-all-candidates still work. */
export function muteAllLiveInterviewCandidates(roomId, payload = {}) {
  const body = { ...payload, mute_all: true };
  return requestJson(roomEndpoint(roomId, "/participants/mute-candidates"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

/** Mute or unmute one participant by LiveKit identity (candidates only; enforced server-side). Uses mute-candidates + JSON identity so proxies do not mishandle path-encoded identities. */
export function muteLiveInterviewParticipantCandidate(roomId, participantIdentity, payload = {}) {
  const identity = String(participantIdentity || "").trim();
  if (!identity) {
    throw new Error("Participant identity is required before muting a candidate.");
  }
  return requestJson(roomEndpoint(roomId, "/participants/mute-candidates"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...payload, identity }),
  });
}

export function leaveLiveInterviewParticipant(roomId, payload = {}) {
  return requestJson(roomEndpoint(roomId, "/participant-left"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function saveLiveTranscriptSegment(roomId, payload) {
  return requestJson(roomEndpoint(roomId, "/transcript-segments"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function reconcileLiveInterviewTranscriptWorker(roomId, payload = {}) {
  const role = String(payload.role || payload.actor_role || "interviewer").trim().toLowerCase();
  const effectiveActorEmail =
    role === "candidate" ? "" : payload.actor_email || payload.actorEmail || getLiveInterviewActorEmail();
  return requestJson(roomEndpoint(roomId, "/transcript/reconcile"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...buildActorRequestHeaders({}, effectiveActorEmail),
    },
    body: JSON.stringify({
      ...payload,
      actor_email: effectiveActorEmail || payload.actor_email || "",
    }),
  });
}

export function getLiveInterviewTranscript(
  roomId,
  {
    actorEmail,
    actorIdentity,
    role = "interviewer",
    inviteToken,
    interviewerToken,
    bookingId,
    signal,
  } = {}
) {
  const effectiveActorEmail = role === "candidate" ? "" : actorEmail || getLiveInterviewActorEmail();
  const params = new URLSearchParams({
    actor_email: effectiveActorEmail || "",
    role,
  });
  if (actorIdentity) {
    params.set("actor_identity", actorIdentity);
  }
  if (inviteToken) {
    params.set("invite_token", inviteToken);
  }
  const headers = buildActorRequestHeaders({}, effectiveActorEmail);
  if (interviewerToken && role !== "candidate") {
    // The API verifies the booking action token server-side. Keep it out of
    // the frequently polled URL so normal access logs do not repeat it.
    headers.set("Authorization", `Bearer ${interviewerToken}`);
  }
  if (bookingId) {
    params.set("booking_id", bookingId);
  }
  return requestJson(roomEndpoint(roomId, `/transcript?${params.toString()}`), {
    headers,
    signal,
    suppressAbortLog: Boolean(signal),
  });
}

function buildVerifiedStaffHeaders(interviewerToken = "") {
  const headers = buildRequestHeaders();
  const safeToken = String(interviewerToken || "").trim();
  if (safeToken) {
    headers.set("Authorization", `Bearer ${safeToken}`);
  }
  return headers;
}

export function getLiveInterviewEligibleApplications(roomId, { interviewerToken } = {}) {
  return requestJson(roomEndpoint(roomId, "/eligible-applications"), {
    headers: buildVerifiedStaffHeaders(interviewerToken),
  });
}

export function getLiveInterviewApplicationContextStatus(
  roomId,
  applicationId,
  { interviewerToken } = {}
) {
  const headers = buildVerifiedStaffHeaders(interviewerToken);
  headers.set("Content-Type", "application/json");
  return requestJson(roomEndpoint(roomId, "/application-context"), {
    method: "POST",
    headers,
    body: JSON.stringify({ application_id: applicationId }),
  });
}

export function generateLiveInterviewFinalSummary(
  roomId,
  applicationId,
  { interviewerToken, signal } = {}
) {
  const headers = buildVerifiedStaffHeaders(interviewerToken);
  headers.set("Content-Type", "application/json");
  return requestJson(roomEndpoint(roomId, "/final-summary"), {
    method: "POST",
    headers,
    signal,
    body: JSON.stringify({ application_id: applicationId }),
  });
}

export function generateLiveInterviewAiQuestions(
  roomId,
  {
    applicationId,
    mode = "mixed",
    questionCount,
    generationIntent,
    progressCheckpointReference,
  } = {},
  { interviewerToken, signal } = {}
) {
  const headers = buildVerifiedStaffHeaders(interviewerToken);
  headers.set("Content-Type", "application/json");
  const body = {
    application_id: applicationId,
    mode,
  };
  if (questionCount !== undefined && questionCount !== null) {
    body.question_count = questionCount;
  }
  if (generationIntent !== undefined && generationIntent !== null) {
    body.generation_intent = generationIntent;
  }
  if (progressCheckpointReference !== undefined && progressCheckpointReference !== null) {
    body.progress_checkpoint_reference = progressCheckpointReference;
  }

  return requestJson(roomEndpoint(roomId, "/ai-questions"), {
    method: "POST",
    headers,
    body: JSON.stringify(body),
    signal,
    suppressAbortLog: Boolean(signal),
  });
}

export function markLiveInterviewAiQuestionAsAsked(
  roomId,
  { applicationId, questionReference } = {},
  { interviewerToken, signal } = {}
) {
  const headers = buildVerifiedStaffHeaders(interviewerToken);
  headers.set("Content-Type", "application/json");
  return requestJson(roomEndpoint(roomId, "/ai-question-checkpoints"), {
    method: "POST",
    headers,
    body: JSON.stringify({
      application_id: applicationId,
      question_reference: questionReference,
    }),
    signal,
    suppressAbortLog: Boolean(signal),
  });
}

export function analyzeLiveInterviewAnswer(
  roomId,
  { applicationId, checkpointReference } = {},
  { interviewerToken, signal } = {}
) {
  const headers = buildVerifiedStaffHeaders(interviewerToken);
  headers.set("Content-Type", "application/json");
  return requestJson(roomEndpoint(roomId, "/ai-answer-analysis"), {
    method: "POST",
    headers,
    body: JSON.stringify({
      application_id: applicationId,
      checkpoint_reference: checkpointReference,
    }),
    signal,
    suppressAbortLog: Boolean(signal),
  });
}

export function getLiveInterviewSkillCoverage(
  roomId,
  applicationId,
  { interviewerToken, signal } = {}
) {
  const params = new URLSearchParams({ application_id: String(applicationId || "").trim() });
  return requestJson(roomEndpoint(roomId, `/skill-coverage?${params.toString()}`), {
    headers: buildVerifiedStaffHeaders(interviewerToken),
    signal,
    suppressAbortLog: Boolean(signal),
  });
}

export function listLiveInterviewChatMessages(
  roomId,
  { actorEmail, actorIdentity, role = "interviewer", inviteToken, limit = 500 } = {}
) {
  const effectiveActorEmail = role === "candidate" ? "" : actorEmail || getLiveInterviewActorEmail();
  const params = new URLSearchParams({
    actor_email: effectiveActorEmail || "",
    role,
    limit: String(limit),
  });
  if (actorIdentity) {
    params.set("actor_identity", actorIdentity);
  }
  if (inviteToken) {
    params.set("invite_token", inviteToken);
  }
  return requestJson(roomEndpoint(roomId, `/chat-messages?${params.toString()}`), {
    headers: buildActorRequestHeaders({}, effectiveActorEmail),
  });
}

export function saveLiveInterviewChatMessage(roomId, payload = {}) {
  return requestJson(roomEndpoint(roomId, "/chat-messages"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function uploadLiveInterviewChatAttachment(
  roomId,
  file,
  { actorEmail, actorIdentity, role = "interviewer", inviteToken } = {}
) {
  if (!file) {
    throw new Error("Choose a file before sharing it.");
  }
  const effectiveActorEmail = role === "candidate" ? "" : actorEmail || getLiveInterviewActorEmail();
  const body = new FormData();
  body.append("file", file);
  body.append("actor_email", effectiveActorEmail || "");
  body.append("actor_identity", actorIdentity || "");
  body.append("role", role || "interviewer");
  if (inviteToken) {
    body.append("invite_token", inviteToken);
  }
  return requestJson(roomEndpoint(roomId, "/chat-attachments"), {
    method: "POST",
    headers: buildActorRequestHeaders({}, effectiveActorEmail),
    body,
  });
}

export function getLiveInterviewChatAttachmentDownload(
  roomId,
  attachmentId,
  {
    actorEmail,
    actorIdentity,
    role = "interviewer",
    inviteToken,
    fileName = "",
    inline = false,
  } = {}
) {
  const safeAttachmentId = String(attachmentId || "").trim();
  if (!safeAttachmentId) {
    throw new Error("Attachment id is required before downloading a file.");
  }
  const effectiveActorEmail = role === "candidate" ? "" : actorEmail || getLiveInterviewActorEmail();
  const params = new URLSearchParams({
    actor_email: effectiveActorEmail || "",
    actor_identity: actorIdentity || "",
    role: role || "interviewer",
    file_name: fileName || "",
  });
  if (inviteToken) {
    params.set("invite_token", inviteToken);
  }
  if (inline) {
    params.set("inline", "1");
  }
  return requestJson(
    roomEndpoint(roomId, `/chat-attachments/${encodeURIComponent(safeAttachmentId)}/download?${params.toString()}`),
    {
      headers: buildActorRequestHeaders({}, effectiveActorEmail),
    }
  );
}

export function closeLiveInterviewRoom(roomId, payload = {}) {
  return requestJson(roomEndpoint(roomId, "/close"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function reconcileLiveInterviewStuckRecordings(roomId, payload = {}) {
  return requestJson(roomEndpoint(roomId, "/recordings/reconcile-stuck"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export function getLiveInterviewAuditEvents(roomId, { actorEmail, actorIdentity, role = "interviewer", limit = 200 } = {}) {
  const effectiveActorEmail = role === "candidate" ? "" : actorEmail || getLiveInterviewActorEmail();
  const params = new URLSearchParams({
    actor_email: effectiveActorEmail || "",
    role,
    limit: String(limit || 200),
  });
  if (actorIdentity) {
    params.set("actor_identity", actorIdentity);
  }
  return requestJson(roomEndpoint(roomId, `/audit-events?${params.toString()}`), {
    headers: buildActorRequestHeaders({}, effectiveActorEmail),
  });
}

export function getLiveInterviewHistory(roomId, { actorEmail, actorIdentity, role = "interviewer" } = {}) {
  const effectiveActorEmail = role === "candidate" ? "" : actorEmail || getLiveInterviewActorEmail();
  const params = new URLSearchParams({
    actor_email: effectiveActorEmail || "",
    role,
  });
  if (actorIdentity) {
    params.set("actor_identity", actorIdentity);
  }
  return requestJson(roomEndpoint(roomId, `/history?${params.toString()}`), {
    headers: buildActorRequestHeaders({}, effectiveActorEmail),
  });
}
